import React, { useEffect, useMemo, useRef } from 'react'
import { format } from 'date-fns'
import { ESTADOS } from '../lib/estados'
import { useAuth } from '../contexts/AuthContext'

// Faixa lateral (só desktop) com a agenda do dia da profissional selecionada
// em Nova Marcação — versão reduzida da grelha de AgendaDia.jsx: uma só
// profissional de cada vez e sem drag nem edição de bloqueios, mas com a
// mesma estrutura de grelha e os mesmos cartões de marcação.
const PX_POR_MIN = 1.3
const SLOT_MIN = 30
const AXIS_WIDTH = 34

// Mesma configuração de slots da Agenda do Dia: a grelha cobre o dia inteiro
// (00:00–24:00) para que marcações e bloqueios fora do horário de atendimento
// fiquem visíveis; o horário de atendimento é apenas destacado e serve de alvo
// do auto-scroll.
const GRID_INICIO_MIN = 0
const GRID_FIM_MIN = 24 * 60

function horaParaMinutos(hhmm) {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

function minutosParaHora(min) {
  const h = Math.floor(min / 60).toString().padStart(2, '0')
  const m = (min % 60).toString().padStart(2, '0')
  return `${h}:${m}`
}

function iniciais(nome) {
  return (nome || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0].toUpperCase())
    .join('')
}

// Igual à Agenda do Dia: texto escuro ou claro conforme a luminância da cor.
function corContraste(hex) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const luminancia = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminancia > 0.5 ? '#2C1A0E' : '#FFFFFF'
}

export default function FaixaAgendaDia({
  profissionais,
  profissionalVistoId,
  onMudarProfissionalVisto,
  data,
  horaSelecionada,
  horarioAbertura,
  horarioFecho,
  almocoAtivo,
  horaAlmocoInicio,
  horaAlmocoFim,
  agendamentosDoDia,
  bloqueiosDoDia,
  blocosProvisorios,
  onClicarSlot,
}) {
  const gridRef = useRef(null)
  const { estadosVisiveisAgenda } = useAuth()

  const aberturaMin = horaParaMinutos(horarioAbertura || '09:00')
  const fechoMin = horaParaMinutos(horarioFecho || '18:00')
  const almocoInicioMin = horaParaMinutos(horaAlmocoInicio || '13:00')
  const almocoFimMin = horaParaMinutos(horaAlmocoFim || '14:00')
  const gridInicioMin = GRID_INICIO_MIN
  const gridFimMin = GRID_FIM_MIN
  const alturaTotal = (gridFimMin - gridInicioMin) * PX_POR_MIN

  const profissionalVista = profissionais.find(p => p.id === profissionalVistoId) || null
  const cor = profissionalVista?.cor_agenda || '#8B4A2F'

  // Mesmo critério da Agenda do Dia: filtra pelos estados visíveis ao perfil
  // (as canceladas deixam de ser escondidas à força — aparecem se o perfil as
  // puder ver, tal como na agenda).
  const marcacoesVista = useMemo(
    () => agendamentosDoDia.filter(a =>
      a.profissional_id === profissionalVistoId && estadosVisiveisAgenda.has(a.estado)
    ),
    [agendamentosDoDia, profissionalVistoId, estadosVisiveisAgenda]
  )
  const bloqueiosVista = useMemo(
    () => bloqueiosDoDia.filter(b => b.profissional_id === null || b.profissional_id === profissionalVistoId),
    [bloqueiosDoDia, profissionalVistoId]
  )

  function posicao(inicioISO, fimISO) {
    const inicio = new Date(inicioISO)
    const fim = new Date(fimISO)
    const diaInicio = format(inicio, 'yyyy-MM-dd')
    const diaFim = format(fim, 'yyyy-MM-dd')

    // Bloqueios/marcações de vários dias (ex.: férias de 6 a 10 de Julho) têm
    // de ser recortados ao dia mostrado: sem isto usava-se o getHours() do
    // primeiro dia (09:00) num dia em que o bloqueio ocupa as 24 horas.
    const inicioMin = diaInicio < data ? gridInicioMin : inicio.getHours() * 60 + inicio.getMinutes()
    let fimMin
    if (diaFim > data) {
      fimMin = gridFimMin
    } else {
      const bruto = fim.getHours() * 60 + fim.getMinutes()
      // Fim à meia-noite conta como fim do dia, não como 00:00 do próprio dia.
      fimMin = bruto === 0 ? gridFimMin : bruto
    }

    return {
      top: (Math.max(inicioMin, gridInicioMin) - gridInicioMin) * PX_POR_MIN,
      height: Math.max(4, (Math.min(fimMin, gridFimMin) - Math.max(inicioMin, gridInicioMin)) * PX_POR_MIN),
      inicioMin,
      fimMin,
    }
  }

  // Distribui as marcações lado a lado quando os horários se sobrepõem, em vez
  // de ficarem empilhadas na mesma posição (ilegíveis). Mesma técnica da
  // Agenda do Dia: clusters de marcações mutuamente sobrepostas, greedy sobre
  // a primeira coluna livre.
  function layoutComSobreposicao(lista) {
    const itens = lista
      .map(ag => ({ ag, ...posicao(ag.data_hora_inicio, ag.data_hora_fim) }))
      .sort((a, b) => a.inicioMin - b.inicioMin || a.fimMin - b.fimMin)

    const resultado = []
    let colunas = []
    let cluster = []
    let clusterFimMax = -Infinity

    function fecharCluster() {
      if (cluster.length === 0) return
      const totalCols = colunas.length
      for (const item of cluster) resultado.push({ ...item, totalCols })
      cluster = []
      colunas = []
    }

    for (const item of itens) {
      if (item.inicioMin >= clusterFimMax) {
        fecharCluster()
        clusterFimMax = -Infinity
      }
      let colIndex = colunas.findIndex(fim => fim <= item.inicioMin)
      if (colIndex === -1) {
        colIndex = colunas.length
        colunas.push(item.fimMin)
      } else {
        colunas[colIndex] = item.fimMin
      }
      cluster.push({ ...item, colIndex })
      clusterFimMax = Math.max(clusterFimMax, item.fimMin)
    }
    fecharCluster()

    return resultado
  }

  // Geometria horizontal de uma marcação dentro do seu cluster, já a contar
  // com a coluna das horas à esquerda.
  function geometriaX(colIndex, totalCols) {
    const PAD = 3
    const GAP = 3
    const base = `100% - ${AXIS_WIDTH}px`
    if (totalCols <= 1) {
      return { left: `${AXIS_WIDTH + PAD}px`, width: `calc(${base} - ${PAD * 2}px)` }
    }
    const largura = `calc((${base} - ${PAD * 2}px - ${(totalCols - 1) * GAP}px) / ${totalCols})`
    const esquerda = `calc(${AXIS_WIDTH + PAD}px + ${colIndex} * ((${base} - ${PAD * 2}px - ${(totalCols - 1) * GAP}px) / ${totalCols} + ${GAP}px))`
    return { left: esquerda, width: largura }
  }

  // Auto-scroll para a hora selecionada (ou início do horário de atendimento)
  // sempre que se troca de aba/profissional ou de dia. A grelha cobre o dia
  // inteiro, por isso sem isto abriria às 00:00.
  useEffect(() => {
    if (!gridRef.current) return
    const alvoMin = horaSelecionada ? horaParaMinutos(horaSelecionada) : aberturaMin
    const alvoPx = Math.max(0, (alvoMin - gridInicioMin - 45) * PX_POR_MIN)
    gridRef.current.scrollTop = alvoPx
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profissionalVistoId, data, aberturaMin])

  function aoClicarGrelha(e) {
    const rect = e.currentTarget.getBoundingClientRect()
    // Cliques sobre a coluna das horas não escolhem hora.
    if (e.clientX - rect.left < AXIS_WIDTH) return
    const y = e.clientY - rect.top + e.currentTarget.scrollTop
    const minCliques = gridInicioMin + y / PX_POR_MIN
    const minArredondado = Math.round(minCliques / 5) * 5
    onClicarSlot(minutosParaHora(Math.max(0, Math.min(24 * 60 - 5, minArredondado))), profissionalVistoId)
  }

  // Marcador da hora atual (só quando a faixa mostra o dia de hoje)
  const agora = new Date()
  const agoraEHoje = data === format(agora, 'yyyy-MM-dd')
  const agoraTop = (agora.getHours() * 60 + agora.getMinutes() - gridInicioMin) * PX_POR_MIN

  const horasEixo = []
  for (let m = gridInicioMin; m <= gridFimMin; m += 60) {
    horasEixo.push(m)
  }

  // Linhas de slot (30min), como na Agenda do Dia
  const linhasSlot = []
  for (let m = gridInicioMin; m < gridFimMin; m += SLOT_MIN) {
    linhasSlot.push(m)
  }

  return (
    <div style={{
      background: 'var(--cor-branco)',
      border: '1px solid var(--cor-borda)',
      borderRadius: 'var(--raio-lg)',
      overflow: 'hidden',
      // Coluna flexível: a grelha (flex:1) fica com toda a altura que sobrar
      // do cartão, em vez de um tecto fixo de 560px.
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
    }}>
      {/* Abas de profissional */}
      <div style={{
        display: 'flex', gap: '6px',
        padding: '10px 12px',
        borderBottom: '1px solid var(--cor-borda)',
        flexShrink: 0,
        // Com muitas profissionais rola na horizontal em vez de empilhar
        // várias filas de abas e roubar altura à grelha.
        overflowX: 'auto',
      }}>
        {profissionais.map(p => {
          const ativa = p.id === profissionalVistoId
          const corProf = p.cor_agenda || '#8B4A2F'
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onMudarProfissionalVisto(p.id)}
              title={p.nome}
              style={{
                // Mesma leitura da Agenda do Dia: bola com iniciais + o
                // primeiro nome por baixo (só as iniciais não chegavam para
                // distinguir, sobretudo com dois "S").
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                padding: '6px 8px',
                borderRadius: 'var(--raio)',
                border: `1px solid ${ativa ? corProf : 'var(--cor-borda)'}`,
                background: ativa ? `${corProf}1A` : 'var(--cor-branco)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <span style={{
                width: '26px', height: '26px', borderRadius: '50%',
                background: corProf, color: corContraste(corProf),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '10px', fontWeight: '700', flexShrink: 0,
              }}>
                {iniciais(p.nome)}
              </span>
              <span style={{
                fontSize: '11px', lineHeight: 1.2,
                fontWeight: ativa ? '700' : '600',
                color: ativa ? corProf : 'var(--cor-texto)',
                // Sem corte: o primeiro nome aparece inteiro (a fila de abas
                // rola na horizontal se for preciso).
                whiteSpace: 'nowrap',
              }}>
                {p.nome.split(' ')[0]}
              </span>
            </button>
          )
        })}
      </div>

      {!profissionalVistoId ? (
        <p style={{ padding: '20px', fontSize: '13px', color: 'var(--cor-texto-suave)' }}>
          Escolha uma profissional para ver a agenda do dia.
        </p>
      ) : (
        <>
          {/* Nome completo — as abas mostram só o primeiro nome, que pode
              repetir-se entre profissionais. */}
          <p style={{ padding: '8px 12px 0', margin: 0, fontSize: '13px', fontWeight: '600', color: 'var(--cor-texto)', flexShrink: 0 }}>
            {profissionalVista?.nome}
          </p>
          <div
            ref={gridRef}
            onClick={aoClicarGrelha}
            style={{
              position: 'relative',
              margin: '8px 12px 0',
              flex: 1,
              minHeight: '320px',
              overflowY: 'auto',
              border: '1px solid var(--cor-borda)',
              borderRadius: 'var(--raio)',
              // Mesmo cursor do resto do site para coisas clicáveis.
              cursor: 'pointer',
            }}
          >
            <div style={{ position: 'relative', height: `${alturaTotal}px` }}>
              {/* Eixo das horas — coluna própria à esquerda, para os rótulos
                  nunca ficarem por baixo das marcações. */}
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${AXIS_WIDTH}px`, zIndex: 3,
                borderRight: '1px solid var(--cor-borda)',
                background: 'var(--cor-branco)',
                // Clicar na régua das horas não escolhe hora — cursor normal.
                pointerEvents: 'none',
                cursor: 'default',
              }}>
                {horasEixo.map(min => {
                  const foraHorario = min < aberturaMin || min > fechoMin
                  return (
                    <span
                      key={min}
                      style={{
                        position: 'absolute', left: 0, right: 0,
                        top: `${(min - gridInicioMin) * PX_POR_MIN - 5}px`,
                        textAlign: 'center',
                        fontSize: '10px', lineHeight: 1, fontWeight: '600',
                        color: 'var(--cor-texto-suave)',
                        opacity: foraHorario ? 0.45 : 1,
                      }}
                    >
                      {minutosParaHora(min % 1440)}
                    </span>
                  )
                })}
              </div>

              {/* Linhas de slot (30min), com o fundo esbatido fora do horário */}
              {linhasSlot.map(min => (
                <div
                  key={min}
                  style={{
                    position: 'absolute',
                    left: `${AXIS_WIDTH}px`, right: 0,
                    top: `${(min - gridInicioMin) * PX_POR_MIN}px`,
                    height: `${SLOT_MIN * PX_POR_MIN}px`,
                    background: (min < aberturaMin || min >= fechoMin) ? '#FAF7F2' : 'transparent',
                    borderBottom: (min - gridInicioMin) % 60 === 0
                      ? '1px solid var(--cor-borda)'
                      : '1px dashed #E8E0D5',
                    boxSizing: 'border-box',
                    pointerEvents: 'none',
                  }}
                />
              ))}

              {/* Bloco de almoço */}
              {almocoAtivo && (
                <div style={{
                  position: 'absolute',
                  left: `${AXIS_WIDTH}px`, right: 0,
                  top: `${(almocoInicioMin - gridInicioMin) * PX_POR_MIN}px`,
                  height: `${(almocoFimMin - almocoInicioMin) * PX_POR_MIN}px`,
                  background: 'repeating-linear-gradient(45deg, #F5F0E8, #F5F0E8 4px, #EAE3D6 4px, #EAE3D6 8px)',
                  opacity: 0.7,
                  pointerEvents: 'none',
                }} />
              )}

              {/* Linhas-fronteira do horário de atendimento */}
              <div style={{
                position: 'absolute', left: `${AXIS_WIDTH}px`, right: 0, pointerEvents: 'none',
                top: `${(aberturaMin - gridInicioMin) * PX_POR_MIN}px`,
                borderTop: '2px solid var(--cor-principal)', opacity: 0.5,
              }} />
              <div style={{
                position: 'absolute', left: `${AXIS_WIDTH}px`, right: 0, pointerEvents: 'none',
                top: `${(fechoMin - gridInicioMin) * PX_POR_MIN}px`,
                borderTop: '2px solid var(--cor-principal)', opacity: 0.5,
              }} />

              {/* Bloqueios — mesmas cores da Agenda do Dia (âmbar para fecho do
                  espaço, vermelho para bloqueio de profissional). */}
              {bloqueiosVista.map(b => {
                const { top, height } = posicao(b.data_hora_inicio, b.data_hora_fim)
                const isGlobal = b.profissional_id === null
                const isSmall = height < 26
                const corBase = b.cor || (isGlobal ? '#D97706' : '#EF4444')
                const fundo = b.cor
                  ? `repeating-linear-gradient(135deg, ${corBase}22, ${corBase}22 5px, ${corBase}40 5px, ${corBase}40 10px)`
                  : (isGlobal
                      ? 'repeating-linear-gradient(135deg, #FEF3C7, #FEF3C7 5px, #FDE68A 5px, #FDE68A 10px)'
                      : 'repeating-linear-gradient(135deg, #FEE2E2, #FEE2E2 5px, #FECACA 5px, #FECACA 10px)')
                return (
                  <div
                    key={b.id}
                    title={b.motivo || 'Bloqueado'}
                    style={{
                      position: 'absolute',
                      left: `${AXIS_WIDTH + 2}px`, right: '2px',
                      top: `${top}px`, height: `${height - 1}px`,
                      // Deixa passar o clique para a grelha: a hora pode ser
                      // escolhida também por cima de um bloqueio (o aviso de
                      // sobreposição avisa, mas não impede). Ao contrário das
                      // marcações, um bloqueio não tem ecrã de detalhe para
                      // abrir, por isso não intercepta o clique.
                      pointerEvents: 'none',
                      background: fundo,
                      border: `1.5px solid ${corBase}99`,
                      borderLeft: `3px solid ${corBase}`,
                      borderRadius: '5px',
                      overflow: 'hidden',
                      zIndex: 2,
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: isSmall ? 'center' : 'flex-start',
                      padding: isSmall ? '0 6px' : '4px 6px',
                    }}
                  >
                    {!isSmall && (
                      <span style={{
                        fontSize: '10px', fontWeight: '700', color: corBase,
                        letterSpacing: '0.02em',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {b.motivo || 'Bloqueado'}
                      </span>
                    )}
                  </div>
                )
              })}

              {/* Marcações reais — mesmos cartões da Agenda do Dia */}
              {layoutComSobreposicao(marcacoesVista).map(({ ag: a, top, height, colIndex, totalCols }) => {
                const corMarcacao = a.profissionais?.cor_agenda || cor
                const estadoCfg = ESTADOS[a.estado] || { label: a.estado, cor: '#6B7280' }
                const corEstado = estadoCfg.cor
                const isSmall = height < 55
                const { left, width } = geometriaX(colIndex, totalCols)
                const borderLeft = a.estado === 'confirmado'
                  ? `3px solid ${corMarcacao}`
                  : `3px solid ${corEstado}`

                const tooltip = [
                  a.cliente_nome || 'Sem nome',
                  estadoCfg.label,
                  a.servicos?.nome || 'Sem tratamento',
                  `${format(new Date(a.data_hora_inicio), 'HH:mm')} - ${format(new Date(a.data_hora_fim), 'HH:mm')}`,
                  `Sala: ${a.salas?.nome || 'Sem sala'}`,
                  `Tel: ${a.cliente_telefone || a.clientes?.telefone || 'Sem contacto'}`,
                  '(clica para abrir o detalhe em nova aba)',
                ].join('\n')

                function abrirDetalhe(e) {
                  // Não deixa o clique chegar à grelha (senão escolhia hora ao
                  // mesmo tempo que abre o detalhe).
                  e.stopPropagation()
                  window.open(`/admin/agendamento/${a.id}`, '_blank', 'noopener')
                }

                return (
                  <div
                    key={a.id}
                    title={tooltip}
                    onClick={abrirDetalhe}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter') abrirDetalhe(e) }}
                    style={{
                      position: 'absolute',
                      top: `${top}px`,
                      left, width,
                      zIndex: 3 + colIndex,
                      height: `${height - 2}px`,
                      cursor: 'pointer',
                      background: corMarcacao + '18',
                      border: `1.5px solid ${corMarcacao}44`,
                      borderLeft,
                      borderRadius: '6px',
                      padding: isSmall ? '2px 6px 2px 5px' : '5px 8px 5px 6px',
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    {isSmall ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontWeight: '700', fontSize: '11px', color: corMarcacao, flexShrink: 0 }}>
                          {format(new Date(a.data_hora_inicio), 'HH:mm')}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--cor-texto)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {a.cliente_nome || 'Sem nome'}
                        </span>
                        {a.estado !== 'confirmado' && (
                          <span style={{
                            width: '7px', height: '7px', borderRadius: '50%',
                            background: corEstado, flexShrink: 0,
                          }} title={estadoCfg.label} />
                        )}
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', marginBottom: '1px' }}>
                          <span style={{ fontWeight: '700', fontSize: '12px', color: corMarcacao, flexShrink: 0 }}>
                            {format(new Date(a.data_hora_inicio), 'HH:mm')}
                          </span>
                          <span style={{
                            fontWeight: '600', fontSize: '12px', color: 'var(--cor-texto)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                          }}>
                            {a.cliente_nome || 'Sem nome'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span style={{
                            fontSize: '11px', color: 'var(--cor-texto-suave)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                          }}>
                            {a.servicos?.nome}
                          </span>
                          <span style={{
                            fontSize: '10px', fontWeight: '700',
                            color: corEstado, whiteSpace: 'nowrap', flexShrink: 0,
                          }}>
                            {estadoCfg.label}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )
              })}

              {/* Blocos provisórios — serviços já adicionados à sequência, ainda por gravar */}
              {profissionalVistoId && blocosProvisorios.map(b => {
                if (!b.inicio || !b.fim) return null
                const { top, height } = posicao(b.inicio.toISOString(), b.fim.toISOString())
                return (
                  <div
                    key={b.chave}
                    title={b.nome}
                    style={{
                      position: 'absolute', left: `${AXIS_WIDTH + 2}px`, right: '2px', top: `${top}px`, height: `${height}px`,
                      background: 'var(--cor-creme)', opacity: 0.95,
                      border: '2px dashed var(--cor-principal)', borderRadius: '5px',
                      fontSize: '10px', color: 'var(--cor-principal)', fontWeight: '700',
                      padding: '2px 5px', overflow: 'hidden',
                      // Acima das marcações: é o bloco que se está a criar.
                      zIndex: 20,
                      // Clique passa para a grelha — ver nota nos bloqueios.
                      pointerEvents: 'none',
                    }}
                  >
                    {b.nome}
                  </div>
                )
              })}

              {/* Marcador da hora atual */}
              {agoraEHoje && (
                <div style={{
                  position: 'absolute', left: `${AXIS_WIDTH}px`, right: 0,
                  top: `${agoraTop}px`, zIndex: 25,
                  borderTop: '2px solid #DC2626',
                  pointerEvents: 'none',
                }} />
              )}
            </div>
          </div>

          {/* Legenda — mesma informação da Agenda do Dia */}
          <div style={{
            display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center',
            padding: '7px 12px',
            borderTop: '1px solid var(--cor-borda)',
            background: 'var(--cor-creme)',
            fontSize: '10px', color: 'var(--cor-texto-suave)',
            flexShrink: 0,
          }}>
            <span>
              <strong style={{ color: 'var(--cor-texto)' }}>Horário:</strong>{' '}
              {minutosParaHora(aberturaMin)} – {minutosParaHora(fechoMin)}
            </span>
            {almocoAtivo && (
              <span>
                <strong style={{ color: 'var(--cor-texto)' }}>Almoço:</strong>{' '}
                {minutosParaHora(almocoInicioMin)} – {minutosParaHora(almocoFimMin)}
              </span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Bloqueio profissional">
              <span style={{ display: 'inline-block', width: '12px', height: '8px', borderRadius: '2px', background: 'repeating-linear-gradient(135deg, #FEE2E2, #FEE2E2 3px, #FECACA 3px, #FECACA 6px)', border: '1px solid #F87171', flexShrink: 0 }} />
              Bloqueio
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Bloqueio do espaço">
              <span style={{ display: 'inline-block', width: '12px', height: '8px', borderRadius: '2px', background: 'repeating-linear-gradient(135deg, #FEF3C7, #FEF3C7 3px, #FDE68A 3px, #FDE68A 6px)', border: '1px solid #F59E0B', flexShrink: 0 }} />
              Espaço
            </span>
          </div>

          <p style={{ padding: '6px 12px 8px', fontSize: '10px', color: 'var(--cor-texto-suave)', flexShrink: 0, margin: 0 }}>
            Clica num espaço livre para definir a hora de início.
          </p>
        </>
      )}
    </div>
  )
}
